import type { TranslationKeys } from './fr';

export const en: TranslationKeys = {
  // Main menu
  menu: {
    spaces: 'Spaces',
    sources: 'Sources',
    // V4-FRONT-NAMING-WIZARD-VEILLE 2026-06-03 — "Interests" everywhere (user decision, revert of
    // V4-NAMING-VEILLE). Object keys stay technical ids.
    // V4-FRONT-NAMING-THEMATIQUES 2026-06-22 — "Interests" → "Topics" (3rd rename: Watch→Interests→Topics).
    watch: 'Topics',
    positioning: 'Positioning',
    channels: 'Channels',
    newsletter: 'Newsletter',
    automations: 'Automations',
    posts: 'Posts',
    appearance: 'Appearance',
    settings: 'Settings',
    brandConfig: 'Brand identity',
    analytics: 'Analytics',
    engagement: 'Engagement',
    history: 'History',
  },

  brandConfig: {
    title: 'Brand identity',
    description: 'Your colors, fonts and logo — extracted automatically from your site. Your AI-generated carousels and images reuse them to stay consistent with your brand. Editable anytime.',
    landingUrl: 'Landing page URL',
    landingUrlPlaceholder: 'https://your-site.com',
    reExtract: 'Re-extract',
    reExtracting: 'Extracting…',
    statusPending: 'Extraction in progress…',
    statusFailed: 'Extraction failed',
    statusFailedRetry: 'Retry',
    palette: 'Color palette',
    paletteEmpty: 'No colors detected',
    fonts: 'Fonts',
    fontsEmpty: 'No fonts detected',
    images: 'Detected images',
    imagesEmpty: 'No images detected',
    artisticDirection: 'Artistic direction',
    artisticDirectionPlaceholder: 'Describe the desired visual style…',
    artisticDirectionEmpty: 'No artistic direction yet. Click edit to add one.',
    artisticTagPlaceholder: 'Add a tag then press Enter…',
    preview: 'Preview',
    previewTitle: 'Example title',
    previewText: 'Example: your carousels and images will reuse these colors and fonts.',
    previewCta: 'CTA button',
    refreshError: 'Refresh failed',
    copyColorPrefix: 'Copy',
    save: 'Save',
    saveSuccess: 'Brand identity updated',
    saveError: 'Update failed',
    copyHex: 'Copy',
    copied: 'Copied',
    colorRolePrimary: 'Primary',
    colorRoleSecondary: 'Secondary',
    colorRoleAccent: 'Accent',
    colorRoleBackground: 'Background',
    colorRoleText: 'Text',
    colorRoleMuted: 'Muted',
    imageTypeLogo: 'Logo',
    imageTypeHero: 'Hero image',
    imageTypeAdditional: 'Additional image',
    noLandingUrl: 'No URL has been provided for this space yet. Provide one to trigger brand extraction.',
    // UX fix 20-05 — Manual input mode
    manualModeShow: 'No website? Enter manually',
    manualModeHide: 'Hide manual input',
    manualAddColor: 'Add a color',
    manualAddFont: 'Add a font (Google Font name or family)',
    manualAddImage: 'Add an image (public URL)',
    manualFontPlaceholder: 'e.g.: Inter, Roboto, Poppins…',
    manualHint: 'Items added here are saved immediately and used by AI agents.',
    add: 'Add',
    remove: 'Remove',
    replace: 'Replace',
    invalidHex: 'Invalid hex format (e.g.: #5500FF)',
    invalidImageUrl: 'Invalid URL (must start with http(s)://)',
    // V4-BRAND-01 2026-05-29 — extraction failure panel
    extractionFailed: 'We could not analyze your site. You can retry or configure manually.',
    extractionRetry: 'Retry',
    extractionManual: 'Configure manually',
    extractionSlow: 'Extraction is taking longer than expected. You can wait a bit more or retry.',
    extractionReset: 'Extraction reset',
    extractionResetError: 'Could not reset extraction',
    // V4-BRAND-02 2026-05-29 — polling circuit breaker
    pollingLost: 'Connection lost, reconnecting…',
  },
  // Sidebar
  sidebar: {
    account: 'Account',
    logout: 'Log out',
    saveChanges: 'Save changes',
    publicationsComingSoon: 'Publications',
    newsletterComingSoon: 'Newsletter - Coming soon',
    historyTooltip: 'Publications history',
    switchSpace: 'Switch space',
  },

  // V4-ANALYTICS-HISTORY — History panel (right sidebar).
  history: {
    title: 'Publications history',
    metricViews: 'Views',
    metricLikes: 'Likes',
    metricComments: 'Comments',
    metricShares: 'Shares',
    granDaily: 'Daily',
    granWeekly: 'Weekly',
    granMonthly: 'Monthly',
    loading: 'Loading…',
    empty: 'No history data yet. Stats will appear once your posts are published and their metrics collected.',
    weekAbbrev: 'Wk',
    topTopics: 'Top topics',
    topSources: 'Top sources',
    postsActivity: 'Publishing activity',
    totalPosts: 'Total',
    postsLastMonth: 'Last month',
    postsVariation: 'Change',
    globalStats: 'Global statistics',
    positioningTitle: 'Positioning',
    improvePositioning: 'Improve positioning',
    noPositioning: 'No specific positioning defined for this channel.',
    generatePositioning: 'Generate positioning',
    positioningSoon: 'Coming soon',
    openPost: 'Open post',
    noData: 'No data',
    savePositioning: 'Save',
    analyticsUnavailable: 'Analytics unavailable (Zernio add-on required)',
  },

  // Agency
  agency: {
    myAgency: 'My agency',
    members: 'Members',
    billing: 'Billing',
    roleOwner: 'Owner',
    roleMember: 'Member',
    spaceRoleOwner: 'Owner',
    spaceRoleEditor: 'Editor',
    spaceRoleViewer: 'Viewer',
    spacesTitle: 'Spaces management',
    seatsUsed: 'spaces used',
    createSpace: 'Create a space',
    editSpace: 'Edit space',
    deleteSpace: 'Archive space',
    noClientEmail: 'No client email set',
    guestEmailsLabel: 'Guest emails (read-only)',
    addGuestEmail: 'Add an email then press Enter',
    guestEmailsHint: 'These people will have read-only access to this space.',
    invalidEmailFormat: 'Invalid email format',
    firstClient: {
      ctaTitle: 'Create your first client space',
      ctaDesc: 'One space per client: dedicated positioning, sources and posts.',
      title: 'Create your first client space',
      subtitle: 'Each client gets its own isolated, configurable space.',
      nameLabel: 'Client name',
      namePlaceholder: 'e.g. Martin Bakery',
      inviteLabel: 'Invite the client (read-only) — optional',
      inviteHint: 'They can view their space\'s posts without editing them.',
      create: 'Create space',
      creating: 'Creating…',
      later: 'Later',
      createError: 'Failed to create the space',
      createdTitle: 'Space "{name}" created',
      createdDesc: 'Set it up now (positioning, sources, channels) to generate its first posts.',
      configureNow: 'Set up now',
    },
    upgradeSeats: 'Add a seat',
    spaceQuotaReachedHint: 'Space quota reached — add a seat to create a new one.',
    addSeatsTitle: 'Add seats',
    addSeatsDesc: 'Select the number of additional seats to add to your subscription.',
    seatPriceLabel: 'Price per seat',
    seatsLabel: 'Seats',
    addSeatsConfirm: 'Add',
    addSeatsSuccess: 'Seats added successfully!',
    confirmArchive: 'Are you sure you want to archive this space? Data will be kept for 30 days.',
    spaceLimitReached: 'Space limit reached for your subscription.',
    spaceName: 'Space name',
    spaceCreated: 'Space created successfully',
    spaceUpdated: 'Space updated',
    spaceArchived: 'Space archived',
    downgradeToPro: 'Switch back to Pro account',
    downgradeTitle: 'Switch back to Pro account',
    downgradeDescription: 'Choose the space to keep. The other spaces will become inaccessible (recoverable on request).',
    downgradeKeepLabel: 'Space to keep',
    downgradeConfirm: 'Confirm switch to Pro',
    downgradeSuccess: 'Account switched back to Pro',
  },

  // Social networks and channels
  networks: {
    x: 'X (Twitter)',
    twitter: 'X (Twitter)',
    facebook: 'Facebook',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    newsletter: 'Newsletter',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    pinterest: 'Pinterest',
    reddit: 'Reddit',
    googlebusiness: 'Google Business',
    baidu: 'Baidu',
    slack: 'Slack',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    teams: 'Microsoft Teams',
    discord: 'Discord',
    threads: 'Threads',
    bluesky: 'Bluesky',
    other: 'Other'
  },

  // Authentication
  auth: {
    title: 'Welcome back',
    subtitle: 'Sign in to your account to continue',
    emailLabel: 'Email',
    emailPlaceholder: 'name@example.com',
    passwordLabel: 'Password',
    signInButton: 'Sign In',
    signingIn: 'Signing in...',
    errorInvalid: 'Invalid email or password',
    loginErrorTitle: 'Login failed',
    loginErrorDescription: 'Please check your credentials and try again.',
    forgotPassword: 'Forgot password?',
    heroTitle: 'Master your strategic monitoring with AI.',
    heroSubtitle: 'Automate the collection, analysis, and dissemination of your strategic information with our intelligent platform.',
    features: [
      'Real-time multi-source monitoring',
      'Advanced semantic analysis',
      'Automated newsletter generation',
      'Customizable dashboards'
    ],
    copyright: 'Raiso. All rights reserved.',
    sessionExpiredTitle: 'Session Expired',
    sessionExpiredDescription: 'Your session has expired. Please sign in again to continue.',
    accountDeactivatedTitle: 'Access revoked',
    accountDeactivatedDescription: 'Your account has been deactivated or your access to this space was revoked. Contact the agency that invited you.',
    switchToSignup: 'Don\'t have an account?',
    switchToSignupLink: 'Create an account',
    switchToLogin: 'Already have an account?',
    switchToLoginLink: 'Sign in',
    signupTitle: 'Create your account',
    signupSubtitle: 'Choose your plan and get started now',
    fullNameLabel: 'Full name',
    fullNamePlaceholder: 'John Doe',
    nextButton: 'Continue',
    backButton: 'Back',
    stepAccount: 'Account',
    stepPlan: 'Plan',
    stepPayment: 'Payment',
    selectPlan: 'Select this plan',
    selectedPlan: 'Selected plan',
    perMonth: '/month',
    perYear: '/year',
    billingMonthly: 'Monthly',
    billingAnnual: 'Annual',
    annualSavePercent: 'Save {percent}%',
    individualPlansTitle: 'Individual plans',
    agencyPlansTitle: 'Agency plans',
    agencyIncludedSeats: '{n} seat(s) included',
    agencyExtraSeat: '{price}€/extra seat',
    promoCodeLabel: 'Promo code (optional)',
    promoCodePlaceholder: 'Enter your promo code',
    promoCodeApply: 'Apply',
    promoCodeValid: 'Promo code applied!',
    promoCodeInvalid: 'Invalid or expired promo code',
    promoCodeChecking: 'Checking...',
    originalPrice: 'Original price',
    discount: 'Discount',
    totalToPay: 'Total to pay',
    afterDiscount: 'after discount',
    vatLabel: 'VAT (20%)',
    freeAccess: 'Free access thanks to promo code!',
    createAccountButton: 'Create my account',
    createAccountAndPay: 'Create my account and pay',
    redirectingToStripe: 'Redirecting to payment...',
    signupSuccess: 'Account created successfully!',
    signupWelcome: 'Welcome! Your payment has been confirmed, your account is ready.',
    signupPaymentCancelled: 'Payment cancelled. You can finalize your subscription later.',
    signupError: 'Error creating account',
    emailAlreadyExists: 'An account already exists with this email address',
    passwordRuleMinLength: '8 characters minimum',
    passwordRuleUppercase: 'One uppercase letter (A-Z)',
    passwordRuleDigit: 'One digit (0-9)',
    passwordRuleSpecial: 'One special character (!@#...)',
    confirmPasswordLabel: 'Confirm password',
    passwordsMismatch: 'Passwords do not match.',
    passwordsMatch: 'Passwords match',
    passwordSecurityError: 'The password does not meet security requirements.',
    loadPlansError: 'Failed to load plans',
    passwordMinLength: 'Password must be at least 8 characters',
    passwordCopied: 'Password copied',
    passwordSaveReminder: '⚠️ Make sure to save your password somewhere before moving to the next step!',
    creditsPerMonth: 'credits/month',
    watermark: 'Watermark',
    referralCodeLabel: 'Referral code (optional)',
    referralCodePlaceholder: 'Referral code',
    // Pricing cards
    htLabel: 'excl. tax',
    ttcLabel: 'incl. tax',
    vatAtCheckout: 'VAT calculated at checkout',
    monthShort: 'mo',
    billedAnnually: 'billed annually',
    freePrice: 'Free',
    mostPopular: 'MOST POPULAR',
    perExtraClient: '/ extra client',
    // Plan taglines
    taglineFree: 'Discover RAISO for free',
    taglinePro: 'Everything a solo creator needs',
    taglineAgency: 'Manage all your clients from one place',
    // Plan CTAs
    ctaFree: 'Start for free',
    ctaPro: 'Start Pro',
    ctaAgency: 'Start Agency',
    // Free plan features
    featureFree1: '1 user',
    featureFree2: '1 connected social network',
    featureFree3: 'Limited number of generated posts',
    featureFree4: '"via Raiso" mention on your posts',
    featureFree5: 'Essential features only',
    // Pro plan features
    featurePro1: '1 brand / workspace',
    featurePro2: 'All your social networks connected',
    featurePro3: 'Unlimited tracking of your topics and news sources',
    featurePro4: 'Unlimited generated posts',
    featurePro5: 'Publishing calendar',
    featurePro6: 'Automatic scheduling & publishing',
    featurePro7: 'Detailed performance statistics',
    // Agency plan features
    featureAgency1: '10 client sub-accounts',
    featureAgency2: 'Multi-brand',
    featureAgency3: 'Centralized agency dashboard',
    featureAgency4: 'Access management',
    featureAgency5: 'Per-client analytics',
    featureAgency6: 'Multi-account scheduling',
    // Email verification
    verifyEmailTitle: 'Check your email',
    verifyEmailDescription: 'A verification email has been sent to {email}. Click the link to activate your account.',
    resendVerificationEmail: 'Resend verification email',
    checkSpamFolder: 'If you can\'t find the email, remember to check your spam or junk folder.',
    verificationEmailResent: 'Verification email resent!',
    verificationEmailResendError: 'Error sending the email',
    emailVerifiedSuccess: 'Email verified successfully! You can now sign in.',
    emailVerifiedWelcome: 'Email verified successfully!',
    emailVerificationExpired: 'The verification link has expired. Please request a new one.',
    emailVerificationInvalid: 'The verification link is invalid.',
    emailNotVerifiedError: 'Your email is not yet verified. Check your inbox.',
    // Forgot / reset password
    forgotPasswordTitle: 'Forgot your password?',
    forgotPasswordSubtitle: 'Enter your email address and we\'ll send you a link to reset your password.',
    forgotPasswordSubmit: 'Send reset link',
    forgotPasswordSubmitting: 'Sending...',
    forgotPasswordEmailSent: 'If this address is associated with an account, you\'ll receive an email in a few minutes.',
    checkSpamWarning: 'Please check your spam folder if you don\'t receive anything.',
    forgotPasswordBackToLogin: 'Back to login',
    resetPasswordTitle: 'New password',
    resetPasswordSubtitle: 'Choose a new password for your account.',
    resetPasswordSubmit: 'Reset password',
    resetPasswordSubmitting: 'Resetting...',
    resetPasswordSuccess: 'Password reset! You can now sign in.',
    resetPasswordExpired: 'This link has expired or is invalid. Please request a new one.',
    resetPasswordInvalid: 'This link is invalid.',
    resetPasswordError: 'An error occurred. Please try again.',
    requestNewLink: 'Request a new link',
    newPasswordLabel: 'New password',
    confirmNewPasswordLabel: 'Confirm new password',
  },

  // General settings
  settings: {
    title: 'General Settings',
    setupScore: 'Setup Score',
    setupScoreLabel: 'Setup Score',
    setupAnalyze: 'Analyze my setup',
    setupCheckPositioning: 'Positioning defined',
    setupCheckSources: 'At least 1 source added',
    setupCheckChannels: 'At least 1 channel created',
    setupCheckTopics: 'At least 1 topic (short keywords)',
    setupCheckMultipleSources: '3 or more sources',
    setupCheckConnectedChannel: '1 connected channel (Zernio)',
    language: 'Interface Language',
    languageLabel: 'Language',
    typography: 'Typography',
    typographyPlaceholder: 'Choose a font',
    typographyPreview: 'Typography preview - Sample text',
    colorScheme: 'Color palette',
    saveColors: 'Save colors',
    resetColors: 'Reset',
    logo: 'Company Logo',
    logoUrl: 'Logo URL',
    logoUrlPlaceholder: 'https://...',
    saveLogo: 'Save Logo',
    uploadLogo: 'Upload a file',
    chooseFile: 'Choose a file',
    uploading: 'Uploading...',
    orUseUrl: 'or use a URL',
    logoUploaded: 'Logo uploaded successfully',
    logoUploadError: 'Error uploading logo',
    invalidLogoType: 'Unsupported file type (JPEG, PNG, GIF, WebP, SVG)',
    logoTooLarge: 'File is too large (max 5MB)',
    // V4-SETTINGS-01 2026-05-29 — Locked brand sections tooltip
    brandLockedTooltip: 'Set your positioning to unlock this section.',
    locked: 'Locked',
    // V4-FRONT-WIZARD-POST-GEN-HIDE 2026-05-29 — Toggle onboarding tutorial
    onboardingToggleLabel: 'Show onboarding tutorial',
    onboardingToggleDescription: 'Re-display the 4-step guide (Positioning, Topics, Sources, Channels) above the post gallery.',
  },

  // Customization
  customization: {
    colorPickerDescription: 'Select a custom color',
  },

  // Sources
  sources: {
    freePlan: 'Free Plan',
    upgradeToReactivate: 'Upgrade to Pro to reactivate this source',
    title: 'Sources',
    titleTooltip: 'Sources are the feeds Raiso monitors (RSS, websites, YouTube channels) to power your watch and generate content based on news from your topics.',
    add: 'Create your first source',
    suggestButton: 'Generate sources',
    suggestTitle: 'Suggest watch sources',
    suggestDescription: 'Based on your positioning and topics, here are sources we suggest. Uncheck those that don\'t interest you.',
    suggestLoading: 'Generating…',
    suggestEmpty: 'No suggestions available.',
    suggestError: 'Cannot generate source suggestions.',
    suggestErrorAdd: 'Error adding sources',
    suggestSuccess: '{count} source(s) added',
    suggestAdding: 'Adding {count} source(s)…',
    emptyTitle: 'No sources yet',
    emptyDescription: 'Add RSS feeds, websites or YouTube channels to power your watch. AI can also suggest some.',
    suggestNoTopics: 'Add at least one topic first to enable AI suggestions',
    none: 'No sources configured',
    name: 'Source name',
    url: 'URL',
    type: 'Type',
    keywords: 'Keywords',
    keywordsPlaceholder: 'Separate keywords with commas',
    enabled: 'Enabled',
    lastSync: 'Last sync',
    suggestedSources: 'Suggested Sources',
    suggestedSourcesDescription: 'Sources automatically extracted from your topics',
    acceptSuggestion: 'Accept',
    rejectSuggestion: 'Reject',
    deleteSuggestion: 'Delete this suggestion',
    newSource: 'New source',
    newKeyword: 'New keyword',
    exampleName: 'E.g., TechCrunch',
    validateSource: 'Validate this source',
    deleteSuggestionTitle: 'Delete this suggestion',
    previewSource: 'Preview this source',
    tutorialTitle: 'Youtube Tutorial',
    tutorialDescription: 'Learn how to add sources from Youtube videos.',
    deleteSourceTitle: 'Delete this source?',
    deleteSourceDescription: 'Are you sure you want to delete "{title}"? This action is irreversible.',
    recommend: 'Recommend',
    recommendedSources: 'Example sources',
    defineInterestsFirst: 'Please define your topics first',
    examplesDescription: 'These sources are examples to inspire you. Click ✓ to add them or the trash to ignore them.',
    examplesNote: '💡 These suggestions are examples based on your topics. Customize them according to your needs.',
    youtubeIdHelp: 'The YouTube channel ID starts with "UC" followed by 22 characters. You can find it in the channel URL or in the page source code.',
    youtubeIdInvalid: 'Invalid YouTube ID format. The ID must start with "UC" followed by 22 alphanumeric characters.',
    urlInvalid: 'Invalid URL format. The address must start with http:// or https://.',
    validatingSource: 'Validating source...',
    sourceValidationFailed: 'Unable to access this source. Please check the URL or ID.',
    sourceValidationSuccess: 'Source accessible!',
    refreshSource: 'Refresh source',
    syncAll: 'Sync all',
    // Multi-agent generation source traceability
    usedForGeneration: 'Sources used for generation',
    relevance: 'Relevance',
    sourceDetails: 'Source details',
    keyFacts: 'Key facts extracted',
    relevanceHigh: 'High',
    relevanceMedium: 'Medium',
    relevanceLow: 'Low',
    relevanceTooltip: 'Estimated match between this article and your positioning + interests. The higher the score, the closer the article is to your editorial line.',
    staleWarning: '{count} source(s) not synced for over 7 days',
    syncProgress: 'Sync in progress ({count})',
    aiSummary: 'AI Summary',
    postAngles: 'Post angles',
    entities: 'Mentioned entities',
    extractedKeywords: 'Extracted keywords',
    feedItems: 'Collected items',
    feedItemsDescription: 'List of collected items for this source',
    // V4-FEEDITEMS-FILTERS-REDESIGN 2026-07-30 — libellés des filtres (étaient en dur)
    filterScore: 'Score',
    filterDate: 'Since…',
    filterMinScore: 'Minimum score',
    filterAnyScore: 'Any',
    resetFilters: 'Reset all',
    viewItems: 'View items',
    noItems: 'No items found',
    itemCountTooltip: '{count} item(s) collected',
    // V4-SOURCE-ERROR-NO-DIAGNOSTIC — sync statuses (were hardcoded English in the component)
    syncStatusPending: 'Syncing…',
    syncStatusSuccess: 'Synced',
    syncStatusFailed: 'Failed',
    // Actionable messages per code (see src/utils/sourceSyncError.ts)
    syncErrorEmptyContent: 'The source responded but returned no usable content: empty page, anti-bot protection, or missing transcript. Open the URL in a browser to check that it actually displays text.',
    syncErrorChannelNotFound: 'YouTube channel not found: it was deleted or made private. The source has been disabled automatically.',
    syncErrorUrlNotAllowed: 'This URL was refused for security reasons (internal address, disallowed protocol, or blocked domain). Use a public http(s) URL.',
    syncErrorUnknown: 'The last sync failed for an unexpected reason. Try again; if it keeps failing, send the technical detail to support.',
    syncErrorDetail: 'Technical detail',
    syncErrorRetryHint: 'Use the sync button to try again.',
    aiAnalyzed: 'Analyzed by AI',
    qualityScore: 'Quality score',
    keyPoints: 'Key points',
    viewpoints: 'Viewpoints',
    confidence: 'confidence',
    targetAudience: 'Target',
    sourceContext: 'Source',
    risksGuardrails: 'Risks & guardrails',
    maxReached: 'Source limit reached. Upgrade to a higher plan.',
    // V4-SOURCES-I18N-LABELS 2026-05-29 — FINDING-SOURCES-01
    rssFeedUrl: 'RSS feed URL',
    youtubeChannelId: 'Channel ID (canonical)',
    podcastUrl: 'Podcast URL',
    rawTextLabel: 'Content',
    urlLabel: 'URL',
    // V4-SOURCES-CTA-CREATE 2026-05-29 — FINDING-CHANNELS-01
    createCTA: 'Create',
    types: {
      RSS: 'RSS',
      API: 'API',
      'Web Scraper': 'Web Scraper',
      Manuel: 'Manual',
    },
  },

  // Watch
  watch: {
    // V4-FRONT-NAMING-WIZARD-VEILLE 2026-06-03 — panel header aligned with "Interests".
    title: 'Topics',
    add: 'New topic',
    none: 'No topics configured. Create your first topic to get started.',
    suggestButton: 'Generate keywords',
    suggestTitle: 'Suggest topics',
    suggestDescription: 'Based on your positioning, here are topics we suggest you monitor. Uncheck those that don\'t interest you.',
    suggestLoading: 'Generating…',
    suggestEmpty: 'No suggestions available.',
    suggestError: 'Cannot generate suggestions. Check your positioning.',
    suggestErrorAdd: 'Error adding topics',
    suggestSuccess: '{count} topic(s) added',
    suggestAdding: 'Adding {count} topic(s)…',
    emptyTitle: 'No topics yet',
    emptyDescription: 'Start by describing the subjects you want to monitor. AI can also suggest some based on your positioning.',
    // New title/description fields
    topicTitle: 'Title',
    topicTitlePlaceholder: 'Ex: AI Watch',
    topicTitleTooltip: 'A short, descriptive title to easily identify this topic.',
    description: 'Description',
    descriptionPlaceholder: 'Ex: Follow the latest news on generative AI, LLMs and their business applications.',
    descriptionTooltip: 'Describe in detail what you want to monitor. This description helps AI find relevant content.',
    // Legacy fields kept for compatibility
    prompt: 'Description',
    promptPlaceholder: 'Ex: Follow the latest news on generative AI and LLMs...',
    keywords: 'Keywords',
    keywordsPlaceholder: 'Ex: ChatGPT, Claude, LLM, generative AI',
    keywordsTooltip: 'Add specific keywords to refine monitoring. These terms will be used to filter and prioritize content.',
    enabled: 'Enabled',
    createdDate: 'Created date',
    deleteTitle: 'Delete this topic?',
    deleteDescription: 'This action is irreversible. The topic and its keywords will be permanently deleted.',
    newKeyword: 'New keyword',
    generatingKeywords: 'Generating keywords with AI...',
    keywordsGenerated: '💡 Keywords are automatically generated by AI upon creation',
    keywordWarningChars: 'Avoid commas and periods — use simple words',
    keywordsGenerating: 'Generating keywords…',
    generateViaAI: 'Generate keywords',
    webSearchItems: 'Web search results',
    webSearchItemsTooltip: '{count} web result(s) collected during generation',
    viewItems: 'View items',
    noItems: 'No web results found',
  },

  // Positioning
  positioning: {
    title: 'General Positioning',
    description: 'Define your global positioning',
    prompt: 'System prompt',
    promptPlaceholder: 'Describe your positioning, tone, values...',
    improve: 'Improve',
    save: 'Save',
    modeChat: 'Agent',
    modeText: 'Text',
    modeBrandConfig: 'Brand identity',
    modeBrandConfigShort: 'Brand',
    modeToggleLabel: 'Mode',
    chatPlaceholder: 'Type your answer...',
    // Chat conversation messages
    chatInitialMessage: 'Hello, I will help you formulate your positioning to help our agents generate truly personalized content. To start, can you tell me if you are using this service personally or on behalf of a company?',
    chatInitialMessageExisting: 'Hi! Your positioning is already set up. I can improve, refine, or enrich it. What would you like to change?',
    chatQuestionPersonal: 'Great! And what is your main field of activity or interest?',
    chatQuestionCompany: 'Perfect! What is your company name and in which industry does it operate?',
    chatQuestionTone: 'Interesting! Now, how would you describe the tone you want for your content? Rather formal and professional, or relaxed and accessible?',
    chatQuestionValues: 'Excellent choice! Can you give me 3 key values or messages you want to convey in your content?',
    chatQuestionAudience: 'Perfect! Last question: who is your target audience? Describe the typical profile of your readers.',
    chatFinalMessage: 'Thank you for all this information! I will now generate a personalized positioning based on your answers. You can always modify it using the text mode at the top right of this page.',
    chatTypePersonal: 'Personal',
    chatTypeCompany: 'Company',
    // New features
    importFromUrl: 'Import from URL',
    extracting: 'Extracting...',
    extract: 'Extract',
    extractedInfo: 'Extracted information',
    analyzeStyle: 'Analyze style',
    analyzeStyleButton: 'Analyze style',
    analyzing: 'Analyzing...',
    styleAnalysis: 'Style analysis',
    pastePostsHere: 'Paste your example posts here, separated by ----',
    suggestChanges: 'Request a modification...',
    suggesting: 'Suggesting...',
    accept: 'Accept',
    reject: 'Reject',
    modify: 'Modify',
    history: 'History',
    historyTitle: 'Version history',
    sendMessage: 'Send message',
    versions: 'Versions',
    noVersions: 'No previous versions',
    restore: 'Restore',
    templateInfo: 'This is a default template. Fill in the fields [in brackets] and check the options.',
    clickToEdit: 'Click to edit the text',
    changesApplied: 'Changes applied',
    versionRestored: 'Version restored',
    // Toolbar buttons
    importUrl: 'Import URL',
    reset: 'Reset',
    // Reset dialog
    resetTitle: 'Reset positioning',
    resetConfirm: 'Are you sure you want to reset your positioning to the default template?',
    warning: 'Warning',
    resetWarning: 'If you don\'t customize the template after this action, the quality of future generated posts will be significantly reduced.',
    resetNote: 'Your current positioning will be saved in the version history.',
    // AI suggestion
    aiSuggestionPlaceholder: 'Ask the AI for a modification...',
    // Chat mode banner
    chatModeInfo: 'Advisory mode — Discuss your positioning without modifying the template. To apply changes, switch to Text mode.',
    chatModeSubtitle: 'Ask questions or request advice. This chat is consultative.',
    // Apply from chat
    applyThisPositioning: 'Apply this positioning',
    positioningApplied: 'Positioning applied successfully',
    applyError: 'Error applying positioning',
    // Apply suggestion with diff
    applySuggestion: 'Apply these changes',
    discardSuggestion: 'Continue without applying',
    suggestionReady: 'Changes ready - review and confirm',
    noSuggestion: 'No changes suggested',
    suggestionError: 'Error generating changes',
    generatingChanges: 'Generating...',
    // DiffView
    accepted: 'accepted',
    unifiedView: 'Unified',
    splitView: 'Side by side',
    clickToReview: 'Click ✓ or ✗ for each line',
    beforeLabel: 'Before',
    expectedResult: 'Expected result',
  },

  // Setup Wizard
  // V4-UX-SETUP-EXPRESS 2026-07-02 (Phase 1.1) — Express onboarding from URL.
  setupExpress: {
    title: 'Set up Raiso in 2 minutes',
    subtitle: 'Give us your website address: we analyze your business, then automatically prepare your watch topics and sources.',
    subtitlePositioningDone: 'Your positioning is ready. We automatically prepare your watch topics and sources.',
    agencyContext: 'Configuring the “{spaceName}” workspace.',
    urlLabel: 'Your website address',
    urlPlaceholder: 'https://my-company.com',
    urlInvalid: 'Your website address looks invalid. Check it or set up manually.',
    // V4-ONBOARDING-PERSONAL-BRANDING
    sourceModeWebsite: 'I have a website',
    sourceModePersonal: 'Personal branding',
    personalPostsLabel: 'Paste 3 to 5 of your posts',
    personalPostsPlaceholder: 'Paste your posts here, separated by a blank line between each…',
    personalPostsHint: 'We analyze your style and themes to build your positioning.',
    postsInvalid: 'Paste at least 2 posts, separated by a blank line.',
    sectorWatchLabel: 'I rely on my industry news',
    sectorWatchHint: 'If enabled, we create watch topics and sources. Otherwise, we rely only on your style.',
    stateNotNeeded: 'Not needed for your profile',
    importPostsCta: 'Import my latest posts',
    importPostsDone: '{count} posts imported.',
    importPostsEmpty: 'No posts to import. Paste your posts manually.',
    start: 'Set up automatically',
    manualLink: 'I’d rather set up manually',
    runningTitle: 'Preparing your workspace…',
    runningSubtitle: 'This takes less than a minute.',
    stepPositioning: 'Analyzing your business',
    stepTopics: 'Watch topics',
    stepSources: 'Content sources',
    stateRunning: 'In progress…',
    stateDone: 'Done',
    stateSkipped: 'Already set up',
    stateFailed: 'To complete manually',
    topicsCreated: '{count} topics added',
    sourcesCreated: '{count} sources added',
    extractFailedTitle: 'We couldn’t analyze your website',
    extractFailedDesc: 'The site may be protected or too slow. Let’s set up your positioning together, step by step.',
    goManual: 'Guided setup',
    retry: 'Try another address',
    doneTitle: 'Your workspace is ready!',
    doneSubtitle: 'Last step: connect a social network to generate and publish your first posts.',
    doneSubtitleChannel: 'You can generate your first posts right now.',
    doneSubtitleNoChannel: 'Connect a social network to generate then publish your posts.',
    ctaGenerate: 'Generate my first posts',
    ctaConnectChannel: 'Connect a social network',
    noChannelWarning: "You won't be able to publish until a network is connected to your account.",
    reviewSetup: 'View / adjust my setup',
    incompleteTitle: 'Setup incomplete',
    incompleteDesc: 'Some steps did not complete. Run the automatic setup again or complete them manually.',
    retryAuto: 'Run the automatic setup again',
    addTopicsManually: 'Add my topics manually',
    addSourcesManually: 'Add my sources manually',
  },
  setupWizard: {
    title: 'Welcome to Raiso!',
    subtitle: 'To get high-quality generated posts, Raiso needs some context. Configure these 4 essential elements to get started:',
    progressLabel: '{completed}/{total} steps completed',
    stepPositioning: 'Positioning',
    stepPositioningDesc: 'Define your identity, tone, and target audience.',
    stepWatch: 'Topics',
    stepWatchDesc: 'Specify the topics you want to communicate about.',
    stepSources: 'Sources',
    stepSourcesDesc: 'Add RSS feeds, websites, or YouTube channels to monitor.',
    stepChannels: 'Channels',
    stepChannelsDesc: 'Configure at least one communication channel (e.g., LinkedIn, X).',
    buttonConfigure: 'Configure',
    buttonCompleted: 'Completed',
    completedTitle: 'Everything is ready! 🎉',
    completedSubtitle: 'Your workspace is set up. You can now generate your first posts.',
    buttonStart: 'Start generating',
    // UX backlog 19-05 — sequential step lock
    lockedStepBadge: 'Locked',
    lockedStepTooltip: 'Complete the step "{previousTitle}" first to unlock this one.',
    lockedStepDescription: 'Available after the previous step.',
    // V4-5 — Brand config CTA at end of wizard
    brandConfigCtaTitle: '🎨 Your colors and fonts, for consistent carousels and images',
    brandConfigCtaDesc: 'Set your palette, fonts and logo: your AI-generated carousels and images reuse them automatically to stay true to your brand. Extracted from your site or entered manually. This is optional — you can already generate posts without it.',
    brandConfigCtaButton: 'Configure',
    // V4-WIZARD-02 2026-05-29 — end-of-wizard CTA
    startCTA: 'Start generating posts',
    // V4-WIZARD-AGENCY-CONTEXT 2026-06-18 — header banner for agency accounts:
    // clarifies WHOSE positioning is being configured (the space = one client).
    agencyContextTitle: '👥 Client workspace',
    agencyContextDesc: 'You are configuring the "{spaceName}" workspace. The positioning, topics and sources below apply to this client.',
  },

  // V4-ONBOARDING-TOUR 2026-06-18 — product tour explanatory bubbles (react-joyride).
  tour: {
    // Navigation buttons
    back: 'Back',
    close: 'Close',
    last: 'Finish',
    next: 'Next',
    skip: 'Skip',
    // Steps (agency path)
    spaceSwitcherTitle: 'Your client workspaces',
    spaceSwitcherDesc: 'Each workspace is one of your clients. Switch here to configure and publish for the right client.',
    spacesManageTitle: 'Manage your workspaces',
    spacesManageDesc: 'Create, rename or archive your workspaces here, and invite your clients in read-only.',
    // Common steps
    positioningTitle: 'Positioning',
    positioningDesc: 'Define your identity, tone and audience. It\'s the foundation of everything Raiso generates.',
    watchTitle: 'Topics',
    watchDesc: 'Specify the topics you want to communicate about: Raiso monitors the related news.',
    sourcesTitle: 'Sources',
    sourcesDesc: 'Add RSS feeds, websites or YouTube channels: Raiso draws on them to generate relevant posts.',
    channelsTitle: 'Channels',
    channelsDesc: 'Connect your networks (LinkedIn, X, Instagram…) to publish directly from Raiso.',
    engagementTitle: 'Engagement',
    engagementDesc: 'Find here the comments received on your publications to reply to them.',
    generateTitle: 'Generate posts',
    generateDesc: 'Once your setup is ready, start generating posts here. You can review, edit and publish them.',
  },

  // Onboarding - Guided questions flow
  onboarding: {
    // Sequential lock tooltips (chantier C 2026-05)
    lockedTooltip: "Complete this step first to continue",
    // V4-NAMING-VEILLE 2026-05-29
    lockedTopicsTooltip: "Define your positioning first to unlock watch",
    lockedSourcesTooltip: "Add at least one topic to unlock sources",
    lockedChannelsTooltip: "Add at least one source to unlock communication channels",
    lockedPostsTooltip: "Connect at least one channel to unlock the post gallery",
    lockedAnalyticsTooltip: "Generate your first post to unlock analytics",
    lockedEngagementTooltip: "Generate your first post to unlock engagement (comments)",
    lockedPublicationsTooltip: "Generate your first post to unlock the publication queue",
    comingSoonTooltip: "This feature will be available soon",
    readOnlyTooltip: "You are viewing this space in read-only mode",
    // Phase 1.4 — persistent global progress widget (OnboardingProgressWidget)
    setupProgress: {
      title: "Your setup",
      resumePrefix: "Pick up at",
      continueBtn: "Continue",
      expand: "Show progress",
      stepPositioning: "Positioning",
      stepTopics: "Topics",
      stepSources: "Sources",
      stepChannels: "Channels",
      optional: "optional",
      reopenAssistant: "Reopen the assistant",
      dragHint: "Drag to move",
    },
    // Step messages
    steps: {
      welcome: {
        message: "Hello! 👋 I'm your positioning assistant. I'll ask you a few questions to create a personalized profile that will help our AI agents generate content truly tailored to your style.\n\nReady to start?",
        startButton: "Let's go!",
      },
      usageType: {
        message: "Great! To start, are you using this service for yourself or for a company?",
        personal: "👤 Personal use",
        company: "🏢 For a company",
        custom: "✏️ Define myself",
      },
      companyUrl: {
        message: "Awesome! To better understand your company, do you have a website? I can analyze your homepage or \"About\" page to automatically extract key information.",
        hasUrl: "🔗 Yes, I'll provide the URL",
        noUrl: "✏️ No, I'll describe it manually",
        placeholder: "https://www.mycompany.com",
      },
      companyName: {
        message: "No problem! Can you give me your company name and briefly describe its activity?",
        placeholder: "E.g., 3615 Future is an AI consulting firm that helps companies with their digital transformation.",
      },
      personalDomain: {
        message: "Got it! What's your main field of activity or expertise? This will help me personalize your positioning.",
        placeholder: "E.g., Digital marketing, Web development, Coaching...",
      },
      purpose: {
        message: "Alright! What's your main goal on social media?",
        brandAwareness: "📢 Build brand awareness",
        thoughtLeadership: "💡 Share my expertise",
        leadGeneration: "🎯 Generate leads",
        community: "🤝 Build community",
        recruitment: "👥 Recruit / Employer branding",
        other: "✨ Other goal",
      },
      tone: {
        message: "Excellent! How do you want your content to be perceived? Choose the tone that best fits you.",
        professional: "👔 Professional and expert",
        friendly: "😊 Friendly and accessible",
        inspiring: "✨ Inspiring and motivating",
        provocative: "🔥 Provocative and disruptive",
        educational: "📚 Educational and informative",
        humorous: "😄 Humorous and light",
        custom: "✏️ Define myself",
      },
      values: {
        message: "Perfect! What are the 3 key values or messages you want to convey in your content?\n\nYou can separate them with commas.",
        placeholder: "E.g., Innovation, Authenticity, Customer excellence",
      },
      audience: {
        message: "Last question! Who is your target audience? Describe the typical profile of people you want to reach.",
        placeholder: "E.g., Tech SME executives, 35-55 years old, interested in innovation and digital transformation",
      },
      summary: {
        message: "Thank you for all this information! 🎉\n\nHere's what I understood:\n{summary}\n\nI'll now generate your personalized positioning. You can always modify it later using the text mode at the top right of this page.",
        confirm: "Generate my positioning",
        restart: "Start over",
      },
      complete: {
        message: "✅ Your positioning has been successfully generated!\n\nYou can view and edit it using the text mode at the top right of this page. Our AI agents will now use this information to create personalized content.",
      },
    },
    // Summary labels
    summaryLabels: {
      usage: "Usage",
      usagePersonal: "Personal",
      usageCompany: "Company",
      company: "Company",
      domain: "Domain",
      companyDetected: "Company (detected)",
      sector: "Sector",
      purpose: "Goal",
      tone: "Tone",
      values: "Values",
      audience: "Audience",
    },
    // Purpose labels
    purposeLabels: {
      brand_awareness: "Build brand awareness",
      thought_leadership: "Share my expertise",
      lead_generation: "Generate leads",
      community: "Build community",
      recruitment: "Recruit / Employer branding",
      other: "Other",
    },
    // Tone labels
    toneLabels: {
      professional: "Professional and expert",
      friendly: "Friendly and accessible",
      inspiring: "Inspiring and motivating",
      provocative: "Provocative and disruptive",
      educational: "Educational and informative",
      humorous: "Humorous and light",
    },
    // Progress checklist
    checklist: {
      progression: "Progress",
      usageType: "Usage type",
      identityCompany: "Company",
      identityPersonal: "Field of activity",
      purpose: "Goal",
      tone: "Tone",
      values: "Values",
      audience: "Audience",
      personal: "Personal",
      company: "Company",
    },
    // Short labels for checklist
    checklistShort: {
      brand_awareness: "Awareness",
      thought_leadership: "Expertise",
      lead_generation: "Leads",
      community: "Community",
      recruitment: "Recruitment",
      other: "Other",
      professional: "Pro",
      friendly: "Friendly",
      inspiring: "Inspiring",
      provocative: "Provocative",
      educational: "Educational",
      humorous: "Humorous",
    },
    // Generation messages
    generating: "✨ Generating your personalized positioning...",
    generationSuccess: "✅ Positioning generated successfully!",
    generationError: "❌ Error generating positioning",
    urlAnalyzing: "🔍 Analyzing URL...",
    urlSuccess: "✅ Information extracted successfully!",
    urlError: "❌ Unable to analyze this URL",
    urlErrorContinue: "⚠️ I couldn't analyze this site. No worries, let's continue with the information you'll provide!",
    urlInvalid: "⚠️ Invalid URL format. Please enter your company name and activity manually.",
    restartOnboarding: "Restart assistant",
    describeGoal: "Describe your goal...",
  },

  // Communication channels
  channels: {
    connect: 'Connect',
    create: 'Create',
    myAccountPrefix: 'My ',
    accountPrefix: 'My ',
    alreadyConnected: 'already connected',
    noPositioning: 'Channel-specific positioning not defined',
    noPositioningTooltip: 'This channel will use the general positioning. You can fine-tune a dedicated positioning for this network for posts that better match its tone.',
    upgradeToReactivate: 'Upgrade to Pro to reactivate this channel',
    title: 'Communication Channels',
    add: 'Create your first channel',
    none: 'No channels configured',
    emptyTitle: 'No communication channels yet',
    emptyDescription: 'Connect LinkedIn, X, Instagram, Slack or another channel to publish your content.',
    customName: 'Channel name',
    customNamePlaceholder: 'E.g., My LinkedIn Pro Account',
    network: 'Social network',
    messagingPlatform: 'Messaging platform',
    profileName: 'Profile name',
    profileNamePlaceholder: 'E.g., @mycompany',
    groupName: 'Group/channel name',
    groupNamePlaceholder: 'E.g., Marketing Team',
    profileUrl: 'Profile URL',
    profileUrlPlaceholder: 'https://...',
    apiKey: 'API Key (optional)',
    apiKeyPlaceholder: 'API key for automation',
    webhookUrl: 'Webhook URL (optional)',
    webhookUrlPlaceholder: 'https://hooks.slack.com/...',
    positioning: 'Specific positioning (optional)',
    positioningPlaceholder: 'Describe the specific positioning on this network...',
    dateFrom: 'Start date for sources',
    dateFromHelp: 'Web sources will be filtered from this date',
    imageProvider: 'Image generation provider',
    imageProviderHelp: 'Provider used to generate images for this channel',
    customNameLabel: 'Custom name',
    customNameEdit: 'E.g., My Company',
    languages: 'Communication languages',
    languagesLabel: 'Languages',
    deleteConfirm: 'Delete this channel?',
    deleteDescription: 'Are you sure you want to delete the channel',
    deleteDescriptionFull: 'on {network}? This action is irreversible.',
    deleteAction: 'This action is irreversible.',
    newChannel: 'New channel',
    channelType: 'Channel type',
    external: 'External (Social networks)',
    internal: 'Internal (Messaging)',
    externalChannels: 'EXTERNAL CHANNELS',
    internalChannels: 'INTERNAL CHANNELS',
    improveWithAI: 'Improve positioning',
    improvingPositioning: 'Generating...',
    positioningImproved: 'Positioning generated successfully',
    profileUrlDisabled: 'Feature coming soon',
    apiKeyDisabled: 'Feature coming soon',
    oauthDisabled: 'OAuth connection coming soon',
    post: 'post',
    posts: 'posts',
    postCountTooltip: '{count} post(s) generated for this channel',
    positioningGlobalRequired: 'Please define a global positioning in your space settings first.',
    positioningError: 'Error generating positioning.',
    automatable: 'Automatable',
    manualOnly: 'Manual',
    // V4-CHANNELS-MEDIA-CAPABILITIES — media formats publishable via Zernio per network
    capabilitiesLabel: 'Publishable formats',
    capImages: 'Images',
    capCarousel: 'Carousels',
    capVideo: 'Video',
    capGif: 'GIF',
    capNone: 'Text only',
    maxReached: 'Channel limit reached. Upgrade to a higher plan.',
    reactivateLimitReached: 'Limit reached — upgrade to Pro to reactivate this channel',
    downgradeBanner: 'Some channels have been disabled after downgrading to Free Plan. Upgrade to Pro to reactivate them all.',
    // V4-CHANNELS-PENDING-SKELETON 2026-05-29 — FINDING-CHANNELS-02
    creatingChannel: 'Creating channel…',
  },
  newsletter: {
    title: 'Newsletter Configuration',
    description: 'Customize your newsletter',
    template: 'Newsletter template',
    templatePlaceholder: 'Describe the format and style of your newsletter...',
    generate: 'Generate a newsletter',
    filterByChannels: 'Filter by channels',
    allChannels: 'All channels',
    save: 'Save',
    send: 'Send',
    sendNewsletter: 'Send newsletter',
    selectDate: 'Select a date',
    cancel: 'Cancel',
    validate: 'Validate',
    scheduledFor: 'Scheduled for',
    addImage: 'Add an image',
    replaceImage: 'Replace image',
    generateImage: 'Generate with AI',
    generatingImage: 'Generating...',
    selectStyle: 'Select style',
    imageGenerated: 'Image generated successfully',
    imageGenerationError: 'Generation error',
    uploadImage: 'Upload an image',
    orGenerateWithAI: 'or generate with AI',
    containsImage: 'Contains an image',
    edit: 'Edit',
    copy: 'Copy',
    saved: 'Save',
    deleteImage: 'Delete image',
    generateFromPosts: 'Generate from posts',
    generateFromQueue: 'Generate from {count, plural, =0 {posts} one {# post} other {# posts}} in queue',
    // Newsletter modal
    modalTitle: 'Newsletter',
    modalDescription: 'Create and schedule a newsletter with selected posts',
    introduction: 'Introduction',
    introductionPlaceholder: 'Write your introduction here...',
    posts: 'Posts',
    postsCount: 'Posts ({count})',
    dragAndDropPosts: 'Drag and drop posts here',
    textMode: 'Text Mode',
    visualMode: 'Visual Mode',
    preview: 'Preview',
    modeConstruction: 'Construction Mode',
    previewMode: 'Preview',
    htmlMode: 'HTML Code',
    enterHtmlCode: 'Enter your HTML code...',
    chooseSendDate: 'Choose send date',
    settings: 'Settings',
    generateWithAI: 'Generate with AI',
    addPostsFirst: 'Add posts first',
    emailTitle: 'Newsletter - Your Daily Monitoring',
    dailyHeadline: 'Your Daily Monitoring',
    dailySubheadline: 'Essential news, directly in your inbox',
    copyright: '© 2024 - All rights reserved',
    footerText: 'You are receiving this email because you subscribed to our newsletter',
    unsubscribe: 'Unsubscribe',
    defaultIntro: 'Here are the latest news and trends of the week...',
    improveWithAi: 'Improve text',
    improving: 'Improving...',
    improveDialogTitle: 'Improve with AI',
    improveDialogDescription: 'Describe how you want to improve this post',
    improveInstructionsPlaceholder: 'E.g.: Make it punchier, add emojis...',
    improveButton: 'Improve',
    improveSuggestions: {
      moreConcise: 'More concise',
      moreEngaging: 'More engaging',
      addHook: 'Add a hook',
      addCTA: 'Add call-to-action',
      shorterForX: 'Shorten for X (280 chars)',
    },
    // Newsletter settings
    settingsTitle: 'Newsletter Settings',
    settingsDescription: 'Configure recipients and HTML template for the newsletter',
    contactsList: 'Contact list (CSV)',
    uploadCSV: 'Upload a CSV file',
    csvFormatExpected: 'Expected format: email, name, firstname (optional)',
    templateHTML: 'HTML Template',
    uploadHTMLTemplate: 'Upload an HTML template',
    templateMustContain: 'The template must contain <div id="introduction"></div> and <div id="posts"></div> tags',
    currentTemplate: 'Current template',
    downloadTemplate: 'Download template',
    templateDownloaded: 'CSV template downloaded',
    contactsUploaded: '{count} contacts imported successfully',
    noValidContactsFound: 'No valid contacts found in the file',
    csvParseError: 'Error reading CSV file',
    templateUploaded: 'HTML template imported successfully',
    templateMissingTags: 'Template must contain id="introduction" and id="posts" tags',
    htmlParseError: 'Error reading HTML file',
  },

  // Automations
  automations: {
    title: 'Automations',
    watch: {
      title: 'Automatic topics',
      enabled: 'Enabled',
      frequency: 'Frequency (days)',
      frequencyDesc: 'Update topics every {days} days',
      period: 'Analysis period (days)',
      periodDesc: 'Analyze the last {period} days (max: {max})',
      preferredHour: 'Update time',
      preferredHourDesc: 'Sources will be automatically refreshed at {hour}:00 (UTC)',
    },
    generation: {
      title: 'Automatic generation',
      enabled: 'Enabled',
      frequency: 'Frequency (days)',
      frequencyDesc: 'Generate posts every {days} days',
      postsCount: 'Number of posts per iteration',
      postsPerIteration: 'Posts per iteration',
      postsDesc: 'Generate {count} posts per iteration',
      channels: 'Target channels',
      selectChannels: 'Select channels',
      noChannels: 'No channels configured. Create a communication channel first.',
    },
    publication: {
      title: 'Automatic publication',
      enabled: 'Enabled',
      frequency: 'Frequency (days)',
      frequencyDesc: 'Publish every {days} days',
      postsCount: 'Number of posts per iteration',
      postsPerIteration: 'Posts per iteration',
      postsDesc: 'Publish {count} posts per iteration',
      channels: 'Target channels',
      selectChannels: 'Select channels',
      noChannels: 'No channels configured. Create a communication channel first.',
    },
    save: 'Save',
    comingSoon: 'Coming soon',
    featureNotAvailable: 'This feature is not yet available',
  },

  // Posts gallery
  posts: {
    unknownChannelLabel: 'Unknown channel',
    gallery: 'Proposals Gallery',
    queue: 'Queue',
    generate: 'Generate',
    generateNew: 'Generate new posts',
    createNew: 'Create a post manually',
    duplicate: 'Duplicate post',
    showImage: 'Show image',
    hideImage: 'Hide image',
    pdfAttachment: 'PDF attached — analysed by AI if generating from visual',
    typeYourContent: 'Jot down a post idea, an anecdote, or paste a draft...',
    draftMode: 'Draft mode',
    publications: 'Publications',
    filterByChannel: 'Filter by channel',
    filterByDate: 'Filter by date',
    filters: 'Filters',
    search: 'Search',
    searchPlaceholder: 'Search posts...',
    searchPlaceholderInSpace: 'Search in {space}...',
    createPost: 'Create post',
    generatePosts: 'Generate posts',
    setupIncompleteTooltip: 'Complete the initial setup to access this feature',
    clear: 'Clear',
    sortBy: 'Sort by',
    sortByChannel: 'By channel',
    sortByDate: 'By date',
    sortByAlphabetical: 'Alphabetical',
    sortChannel: 'Channel',
    sortDate: 'Date',
    sortAlphabetical: 'Alphabetical',
    sortAscending: 'Ascending',
    sortDescending: 'Descending',
    allChannels: 'All channels',
    today: 'Today',
    thisWeek: 'This week',
    thisMonth: 'This month',
    allTime: 'All time',
    sortNewest: 'Newest',
    sortOldest: 'Oldest',
    noPostsGallery: 'No post proposals',
    noPostsAvailable: 'No posts available',
    noPostsDescription: 'Start by clicking "Generate new posts" to create content for your communication channels.',
    noPostsDescriptionReadOnly: 'You\'re viewing this space in read-only mode. Posts published by the team will appear here as soon as they\'re available.',
    noPostsQueue: 'No posts in queue',
    dragHere: 'Drag and drop posts here or click "Add to queue"',
    scheduledFor: 'Scheduled for',
    generatedOn: 'Generated on',
    deleteConfirm: 'Delete this post?',
    deleteDescription: 'This action is irreversible. The post will be permanently deleted.',
    deleteConfirmDesc: 'This action is irreversible. The post will be permanently deleted.',
    unsavedChangesTitle: 'Unsaved changes',
    unsavedChangesDesc: 'You have unsaved changes. If you continue, they will be lost.',
    continueWithoutSaving: 'Continue without saving',
    publish: 'Publish',
    publishCount: 'Publish ({count})',
    editPostDetails: 'Post details',
    editPostDescription: 'Edit the post content, choose a send date and add it to the queue.',
    date: 'Date',
    period: 'Period',
    startDate: 'Start date',
    endDate: 'End date',
    content: 'Content',
    channel: 'Channel',
    scheduledDate: 'Scheduled date',
    external: 'External',
    internal: 'Internal',
    onlyMine: 'My posts',
    onlyMineHint: 'Show only the posts I created',
    createdOn: 'Created on',
    scheduledOn: 'Scheduled on',
    statusFilter: 'Status',
    statusAll: 'All',
    statusDraft: 'To publish',
    statusScheduled: 'Scheduled',
    statusPublished: 'Published',
    statusChipScheduled: 'Scheduled',
    statusChipPublished: 'Published',
    statusChipFailed: 'Failed',
    publishFailedTitle: 'Publishing failed',
    publishFailedGeneric: 'The social network rejected the publication. Check that the channel is connected, then try again.',
    publishFailedAttempts: 'Attempts',
    publishFailedRetryHint: 'Fix the post if needed, then retry with "Publish now".',
    sourceQualityPrimary: 'Based on your sources',
    sourceQualityWebFallback: 'Completed with web search',
    sourceQualityEsFallback: 'Based on your recent feed',
    sourceQualityNone: 'Generated without sources',
    sourceQualityTooltipPrimary: 'The information in this post comes from articles collected by your sources.',
    sourceQualityTooltipWebFallback: 'Your sources did not cover this topic: a web search filled in the information.',
    sourceQualityTooltipEsFallback: 'This post relies on recent content from your feed, without an exact match for the topic.',
    sourceQualityTooltipNone: 'No relevant source found: the content is more generic. Add sources to get better-grounded posts.',
    scheduledCardTooltip: 'This post is already scheduled — manage it from the publishing queue (right column).',
    improveDiffTitle: 'Suggested improvement',
    improveRejected: 'Improvement dismissed — your original text is kept',
    media: {
      replaceMediaTitle: 'Replace media?',
      replaceImageByCarousel: 'This will delete the current image and generate a carousel instead. The image will be permanently removed. Confirm?',
      replaceCarouselByImage: 'This will delete the current carousel and replace it with an image. The carousel will be permanently removed. Confirm?',
      replaceGeneric: 'This will replace the current media. Confirm?',
      replaceConfirm: 'Confirm replacement',
      imageReplacedToast: 'Image replaced by a carousel',
      carouselReplacedToast: 'Carousel replaced by an image',
    },
    deleteImageTitle: 'Delete image',
    editScheduleTitle: 'Edit scheduled date',
    publishedOn: 'Published on',
    publishOn: 'Publish on',
    viewPost: 'View post',
    unschedule: 'Unschedule',
    cannotScheduleInPast: 'Cannot schedule in the past',
    addImage: 'Image',
    replaceImage: 'Replace image',
    generateImage: 'Generate with AI',
    addContentFirst: 'Add content first',
    generatingImage: 'Generating...',
    selectStyle: 'Select style',
    selectModel: 'AI Model',
    imagePrompt: 'Creative prompt',
    imageGenerated: 'Image generated successfully',
    imageGenerationError: 'Generation error',
    uploadImage: 'Upload an image',
    orGenerateWithAI: 'or generate with AI',
    image: {
      label: 'Image',
      promptPreviewLoading: 'Preparing prompt…',
      promptPreviewError: 'Cannot generate prompt',
      promptRateLimited: 'Please try again in a few seconds',
      promptRequired: 'Prompt cannot be empty',
      editPrompt: 'Edit prompt',
      launchGeneration: 'Launch generation',
      promptPlaceholder: 'English prompt for generation…',
      resetPrompt: 'Reset prompt',
      editPlaceholder: 'Edit the image (e.g. change the background, make the person younger)…',
      editLaunch: 'Edit image',
      editSuccess: 'Image edited',
      editError: 'Failed to edit the image',
      editUnsupported: 'This model does not support image editing',
    },
    containsImage: 'Contains an image',
    edit: 'Edit',
    copy: 'Copy',
    improve: 'Improve',
    improveWithAi: 'Improve text',
    improving: 'Improving...',
    improveDialogTitle: 'Improve with AI',
    improveDialogDescription: 'Describe how you want to improve this post',
    improveInstructionsPlaceholder: 'E.g.: Make it punchier, add emojis...',
    improveButton: 'Improve',
    improveSuggestions: {
      moreConcise: 'More concise',
      moreEngaging: 'More engaging',
      addHook: 'Add a hook',
      addCTA: 'Add call-to-action',
      shorterForX: 'Shorten for X (280 chars)',
    },
    saved: 'Save',
    improvedByAI: 'Content improved by AI',
    contentPlaceholder: 'Type your content...',
    errorImprovingContent: 'Error improving content',
    improvedContentSuffix: '✨ Content improved by AI',
    errorUpdatingChannel: 'Error updating channel',
    htmlCopied: 'HTML code copied to clipboard',
    noPostsSelected: 'No posts selected for generation',
    calendar: 'Calendar',
    newsletter: 'Newsletter',
    featureComingSoon: 'This feature will be available soon',
    quickSuggestions: 'Quick suggestions',
    yourInstructions: 'Your instructions',
    currentContent: 'Current content',
    qualityScore: 'Quality score',
    validationUnavailable: 'Not evaluated',
    validationSkippedDesc: 'Automatic validation could not be performed for this post.',
    retryScoring: 'Retry',
    retryScoringTooltip: 'Retry quality scoring for this post',
    rescoringInProgress: 'Scoring…',
    rescoreRateLimited: 'Please try again in a few seconds',
    rescoreSuccess: 'Scoring restarted successfully',
    rescoreError: 'Scoring could not be restarted',
    sourcesUsed: 'Sources used for generation',
    viewDetails: 'View details',
    viewSource: 'View source',
    tone: 'Tone',
    originality: 'Originality',
    clarity: 'Clarity',
    engagement: 'Engagement',
    relevance: 'Relevance',
    overall: 'Overall',
    copyContent: 'Copy content',
    regenerate: 'Regenerate',
    regenerateSuccess: 'New version generated — find it in your gallery.',
    regenerateError: 'Regeneration failed.',
    positioningScore: 'Positioning',
    toneScore: 'Tone',
    overallScore: 'Overall',
    originalityScore: 'Originality',
    complianceFeedback: 'Agent feedback',
    complianceIssues: 'Areas for improvement',
    complianceSuggestions: 'Suggestions',
    // Technical tokens returned by the compliance agent — humanized EN.
    complianceFlags: {
      language_mismatch: 'Wrong language',
      freshness_risk: 'Freshness risk (outdated info)',
      source_incomplete: 'Incomplete or missing source',
      source_missing: 'Missing source',
      unsourced_statistic: 'Unsourced statistic',
      template_hook: 'Generic hook',
      generic_content: 'Generic content',
      tone_mismatch: 'Tone off-brand',
      positioning_drift: 'Off-positioning',
      originality_low: 'Low originality',
      off_topic: 'Off topic',
      no_cta: 'No call to action',
      too_long: 'Too long for the platform',
      too_short: 'Too short',
      hashtags_overuse: 'Too many hashtags',
      buzzwords_overuse: 'Too many buzzwords',
      missing_citation: 'Missing citation',
      duplicate_idea: 'Idea already in the batch',
    },
    noPostsForDay: 'No posts for this day',
    schedule: 'Schedule',
    publishNow: 'Publish now',
    previousPage: 'Previous',
    nextPage: 'Next',
  },

  // V4-ZERNIO-ANALYTICS 2026-05-28
  analyticsTab: {
    title: 'Analytics',
    period: 'Period:',
    days: 'd',
    impressions: 'Impressions',
    reach: 'Reach',
    likes: 'Likes',
    comments: 'Comments',
    engagement: 'Engagement',
    publications: 'Publications',
    onPeriod: 'over last {days} days',
    byNetwork: 'By network',
    postsCount: '{count} posts · {rate} eng.',
    impLabel: 'imp.',
    likesLabel: 'likes',
    noPublications: "No publication on this period — analytics will appear after publication.",
    noSpace: 'No space selected.',
    loadError: 'Unable to load analytics.',
    refresh: 'Refresh',
  },

  // V4-ZERNIO-COMMENTS 2026-05-28
  engagementTab: {
    title: 'Engagement',
    statusUnreplied: 'To reply',
    statusReplied: 'Replied',
    statusAll: 'All',
    emptyUnreplied: 'No comment pending — good job!',
    emptyOther: 'No comment found.',
    noSpace: 'No space selected.',
    loadError: 'Unable to load comments.',
    refresh: 'Refresh',
    anonymous: 'Anonymous',
    replied: 'Replied',
    yourReply: 'Your reply:',
    replyPlaceholder: 'Your reply…',
    cancel: 'Cancel',
    send: 'Send',
    reply: 'Reply',
    like: 'Like',
    alreadyLiked: 'Already liked',
    hide: 'Hide',
    alreadyHidden: 'Already hidden',
    replySuccess: 'Reply published.',
    replyFailed: 'Failed to publish reply.',
    hideSuccess: 'Comment hidden.',
    hideFailed: 'Failed to hide.',
    likeFailed: 'Failed to like.',
  },

  // V4-ZERNIO-ANALYTICS-POST-CARD 2026-05-28
  postAnalytics: {
    title: 'Post analytics',
    viewBtn: 'View post analytics',
    lastSnapshot: 'Latest snapshot',
    history: 'History ({count} snapshots)',
    refresh: 'Refresh',
    refreshed: 'Analytics refreshed.',
    refreshFailed: 'Unable to refresh.',
    loadError: 'Unable to load analytics.',
    impressions: 'Impressions',
    reach: 'Reach',
    likes: 'Likes',
    comments: 'Comments',
    engagement: 'Engagement',
    shares: 'Shares',
    colDate: 'Date',
    colImpressions: 'Imp.',
    colLikes: 'Likes',
    colEngagement: 'Eng.',
    noSnapshot: 'No snapshot yet. Click "Refresh" to fetch live analytics from Zernio.',
  },

  // Colors
  colors: {
    primary: 'Primary color',
    background: 'Surface',
    surface: 'Background',
    border: 'Borders',
    textPrimary: 'Primary text',
    textSecondary: 'Secondary text',
    textLight: 'Light accent',
    accent: 'Dark accent',
    success: 'Success',
    warning: 'Warning',
    danger: 'Danger',
  },

  // Generate post from a pre-uploaded visual (image / carousel / PDF)
  generateFromVisual: {
    button: 'Generate text from this visual',
    buttonShort: 'Generate from visual',
    briefLabel: 'Intent / context (optional)',
    briefPlaceholder: 'E.g. product launch announcement, lesson from a client project, etc.',
    generate: 'Generate text',
    generating: 'Generating…',
    success: 'Text generated from your visual',
    error: 'Failed to generate from the visual',
    confirmOverwrite: 'The current text will be replaced with the text generated from the visual.',
    confirmOverwriteTitle: 'Replace current text?',
    confirm: 'Confirm',
    cancel: 'Cancel',
    regenerate: 'Regenerate',
    phaseInit: 'Preparing…',
    phaseVisual: 'Analysing visual…',
    phaseWriting: 'Drafting the post…',
    phaseCompliance: 'Final validation…',
    phaseFinalize: 'Saving…',
  },

  // Generate modal
  generate: {
    title: 'Generate new posts',
    description: 'Select the channels for which you want to generate content:',
    forcedSectionTitle: 'Steer the generation (optional)',
    forcedTopicLabel: 'Focus on a specific news topic',
    forcedTopicPlaceholder: 'e.g. the GPT-5 release, a recent study on remote work...',
    forcedUrlLabel: 'Force a source link',
    forcedUrlPlaceholder: 'https://example.com/interesting-article',
    postsPerChannel: 'Number of posts per channel',
    postsCount: 'Number of posts',
    periodBack: 'Monitoring period',
    channels: 'Channels',
    noChannelsTitle: 'No channel connected',
    noChannelsHint: 'Connect at least one social network to generate posts.',
    connectChannel: 'Connect a channel',
    cancel: 'Cancel',
    close: 'Close',
    generate: 'Generate',
    start: 'Start generation',
    generating: 'Generating...',
    inProgress: 'Generation in progress...',
    viewProgress: 'View progress',
    error: 'Error generating posts',
    noPostsFound: 'No posts found in the database for the selected criteria.',
    fromToday: 'From today',
    backendNotDeployed: 'The backend server is not deployed yet. Please deploy the Supabase Edge Function via command line: npx supabase functions deploy make-server-b017c5b0',
    serverError: 'Server error',
    errorFetchingPosts: 'Error fetching posts',
    platformPositioning: 'Specific positioning',
    platformPositioningPlaceholder: 'Optional: specific angle or tone for this network (e.g., more corporate on LinkedIn, more casual on X...)',
    imageProvider: 'Image generator',
    // Zernio publishing
    publishNow: 'Publish now',
    schedule: 'Schedule',
    publishing: 'Publishing…',
    publishSuccess: 'Post published successfully',
    publishError: 'Error publishing post',
    validationFailed: 'Validation failed',
    noChannelSelected: 'Please select a channel',
    contentEmpty: 'Content cannot be empty',
    characterLimitExceeded: 'Character limit exceeded',
    mediaRequired: 'A media file is required for this platform',
    titleRequired: 'A title is required for this platform',
    // Validation messages (usePublishValidation)
    noPostSelected: 'No post selected',
    contentEmptyValidation: 'Post content is empty',
    characterLimitDetail: 'Content exceeds the limit ({count}/{max} characters)',
    nearCharacterLimit: 'Near the character limit ({percentage}%)',
    noChannelAssigned: 'No publication channel assigned',
    videoRequiredFor: 'A video file is required for {platform}',
    mediaRequiredFor: 'A media file (image or video) is required for {platform}',
    titleRecommendedFor: 'A title is recommended for {platform}',
    publishAttempts: 'Publish attempts: {count}',
    statusPublished: 'Published',
    statusScheduled: 'Scheduled',
    statusFailed: 'Failed',
    statusDraft: 'Draft',
    // Agent pipeline
    pipeline: {
      memory:     { label: 'Memory',     tooltip: 'Analyses your publication history and memorises your editorial preferences.' },
      knowledge:  { label: 'Research',   tooltip: 'Collects relevant news, trends and sources from your watch feed.' },
      writer:     { label: 'Writing',    tooltip: 'Drafts posts respecting your tone, style and positioning.' },
      diversity:  { label: 'Variety',    tooltip: 'Diversifies formats, angles and structures to avoid repetition.' },
      compliance: { label: 'Guidelines', tooltip: 'Checks conformity with your editorial guidelines and validates positioning.' },
      finalize:   { label: 'Validation', tooltip: 'Finalises and validates all posts before delivering them.' },
    },
  },

  // Platform-specific fields
  platformFields: {
    sectionTitle: 'Platform-specific settings',
    title: 'Title',
    titlePlaceholder: 'Post title',
    subreddit: 'Subreddit',
    subredditPlaceholder: 'r/mysubreddit',
    flairId: 'Flair ID',
    flairIdPlaceholder: 'Flair ID (optional)',
    boardId: 'Pinterest Board',
    boardIdPlaceholder: 'Pinterest board ID',
    linkUrl: 'Link URL',
    linkUrlPlaceholder: 'https://example.com',
    visibility: 'Visibility',
    category: 'Category',
    categoryPlaceholder: 'YouTube category',
    privacyLevel: 'Privacy',
    callToAction: 'Call to action',
    callToActionPlaceholder: 'Learn more, Buy, etc.',
    eventTitle: 'Event title',
    eventTitlePlaceholder: 'Event name',
    eventStart: 'Event start',
    eventEnd: 'Event end',
  },
  toasts: {
    // V4-21 — Upload media
    uploadError: 'Image upload failed. Please retry.',
    // Channels
    channelAddedTitle: 'Channel added',
    channelAddedDesc: 'The channel {name} has been created successfully.',
    channelUpdatedTitle: 'Channel updated',
    channelUpdatedDesc: 'The channel {name} has been modified.',
    channelNoPositioningTitle: 'Channel-specific positioning not defined',
    channelNoPositioningDesc: 'This channel will use the general positioning. Customize a dedicated positioning for better-targeted publications on this network.',
    channelDeletedTitle: 'Channel deleted',
    channelDeletedDesc: 'The communication channel has been deleted.',
    channelAddError: 'Error adding channel',
    channelUpdateError: 'Error updating channel',
    channelDeleteError: 'Error deleting channel',
    channelLoadError: 'Error loading channels',

    // Topics
    topicAddedTitle: 'Topic added',
    topicAddedDesc: 'The topic "{keywords}" has been created successfully.',
    topicUpdatedTitle: 'Topic updated',
    topicUpdatedDesc: 'The topic "{keywords}" has been updated.',
    topicDeletedTitle: 'Topic deleted',
    topicDeletedDesc: 'The topic has been deleted successfully.',
    topicAddError: 'Error adding topic',
    topicUpdateError: 'Error updating topic',
    topicDeleteError: 'Error deleting topic',

    // Sources
    sourceAdded: 'Source added',
    sourceAddedDesc: 'Source "{name}" added successfully.',
    sourceUpdated: 'Source updated',
    sourceUpdatedDesc: 'Source settings have been saved.',
    sourceDeleted: 'Source deleted',
    sourceDeletedDesc: 'Source has been removed from your space.',
    sourceUnlinked: 'Source unlinked',
    sourceUnlinkedDesc: 'Source has been unlinked.',
    sourceAddError: 'Error adding source',
    sourceUpdateError: 'Error updating source',
    sourceDeleteError: 'Error deleting source',
    // V4-SOURCES-LLM-BG 2026-05-29 — FINDING-SOURCES-02
    keywordsBackground: 'LLM service temporarily unavailable. Try again in a moment.',
    noSpaceSelected: 'No space selected',
    // Generation
    generating: 'Generating posts...',
    noChannelSelected: 'No channel selected',
    noPostGenerated: 'No posts generated',
    postsGenerated: '{count} post(s) generated successfully',
    postsGeneratedFeedbackHint: 'If the results don\'t meet your expectations, share your feedback with the positioning agent to improve future generations.',
    partialGeneration: '{delivered} of {requested} requested post(s) generated — content too similar, try a different topic.',
    generationCancelled: 'Generation cancelled',
    generationError: 'Error: {message}',
    generationTimeout: 'Generation timed out',
    generationTimeoutDesc: 'The service is slow right now. You can try again in a moment.',
    imageGenerationTimeout: 'Image generation timed out',
    imageGenerationTimeoutDesc: 'The service is slow right now. You can try again in a moment.',
    fromVisualTimeout: 'Visual-based generation timed out',
    fromVisualTimeoutDesc: 'The service is slow right now. You can try again in a moment.',
    retry: 'Try again',
    // New version detection (stale SPA bundle)
    updateAvailableTitle: 'New version available',
    updateAvailableDesc: 'Reload the page to apply the update.',
    reload: 'Reload',
    // Improvement
    improving: 'Improving post...',
    postImproved: 'Post improved',
    improveError: 'Error: {message}',
    improveTimeout: 'Improvement timed out',
    improveTimeoutDesc: 'The service is slow right now. You can try again in a moment.',
    // Sync
    syncing: 'Syncing sources...',
    syncSuccess: '{count} source(s) synced',
    syncError: 'Error syncing sources',
    sourceSyncing: 'Syncing source...',
    sourceSyncSuccess: 'Source synced successfully',
    sourceSyncError: 'Error syncing source',
    sourceSyncTimeout: 'Sync continues in background...',
    syncAllStarted: 'Sequential sync of {count} source(s)...',
    syncAllCompleted: 'All sources synced successfully',
    syncAllPartial: '{success} source(s) synced, {failed} failed',
    // Copy
    contentCopied: 'Content copied to clipboard',
    copyError: 'Unable to copy content',
    // Posts
    postSaved: 'Post saved',
    postSavedDesc: 'Your changes have been saved successfully',
    postUpdated: 'Post updated',
    postUpdatedDesc: 'Changes have been saved to your gallery',
    postCreated: 'Post created',
    postCreatedDesc: 'Your new post has been added to the gallery',
    postDeleted: 'Post deleted',
    postDeletedDesc: 'The post has been removed from the list',
    postScheduled: 'Post scheduled',
    postScheduledDesc: 'The post has been added to the queue',
    scheduleFailed: 'Scheduling failed',
    scheduleFailedDesc: 'The post could not be scheduled. Please try again.',
    postPublished: 'Post published',
    postPublishedDesc: 'The post has been published successfully via Zernio',
    postPublishError: 'Publish error',
    postPublishErrorDesc: 'Unable to publish the post: {error}',
    postDuplicated: 'Post duplicated',
    postDuplicatedDesc: 'A copy of the post has been added to the gallery',
    postChannelUpdated: 'Channel updated',
    postChannelUpdatedDesc: 'The post is now associated with this new channel',
    imageAdded: 'Image added',
    imageAddedDesc: 'The image has been attached to the post',
    imageRemoved: 'Image removed',
    imageRemovedDesc: 'The image has been removed from the post',
    galleryCleared: 'Gallery cleared',
    galleryClearedDesc: 'All posts have been deleted',
    // Newsletter
    newsletterGenerated: 'Newsletter generated with {count} post(s)',
    // Positioning
    positioning: {
      saveSuccess: 'Positioning saved',
      saveError: 'Error saving positioning',
      loadError: 'Error loading positioning',
    },
    saveError: 'Error saving settings',
    // V4-POS-06 2026-05-29 — URL scraping degraded fallback
    scrapingDegraded: 'Site analysis temporarily unavailable — the assistant continues without it.',
    // General
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Information',
  },

  // Confirm modal
  confirm: {
    clearGalleryTitle: 'Delete confirmation',
    clearGalleryDescription: 'Do you confirm the deletion of all posts in the gallery?',
    clearGalleryQuestion: 'Empty gallery?',
    clearGalleryWarning: 'This action will delete all posts from the gallery.',
    irreversible: 'This action is irreversible.',
    cancel: 'Cancel',
    clearGallery: 'Empty gallery',
    delete: 'Are you sure you want to delete this item?',
    unsavedChanges: 'You have unsaved changes. Do you want to continue?',
    confirmDeleteAllData: 'Are you sure you want to delete all data?',
  },

  // Publications modal
  publications: {
    title: 'Scheduled publications',
    description: 'List of all published posts',
    noPublications: 'No scheduled publications',
    publishedOn: 'Published on',
    at: 'at',
    delete: 'Delete',
    deleteConfirm: 'Delete this publication?',
    deleteDescription: 'This action is irreversible. The publication will be permanently deleted.',
  },

  // Youtube tutorial
  youtubeTutorial: {
    title: 'How to find a YouTube channel ID',
    description: 'Guide to find the unique identifier of a YouTube channel',
    method1Title: 'Method 1: Via the "About" page',
    method1Step1: 'Go to the desired YouTube channel',
    method1Step2: 'Click on the "About" tab',
    method1Step3: 'Click "Share" then "Copy channel ID"',
    method2Title: 'Method 2: Via the channel URL',
    method2Step1: 'Go to the YouTube channel page',
    method2Step2: 'Look at the URL in the address bar',
    method2Step3: 'The channel ID usually starts with "UC" and contains 24 characters',
    method2ExampleUrl: 'Example URL:',
    method3Title: 'Method 3: For channels with @username',
    method3Step1: 'If the channel uses a @username (e.g., @myusername)',
    method3Step2: 'Go to youtube.com/@myusername',
    method3Step3: 'Right-click → "View page source"',
    method3Step4: 'Search for "channelId" in the code (Ctrl+F)',
    method3Step5: 'The channel ID is right after',
    tip: 'Tip:',
    tipText: 'The YouTube channel ID (Canonical ID) always starts with "UC" and contains exactly 24 alphanumeric characters.',
    example: 'Example:',
    gotIt: 'Got it',
  },

  // Common buttons
  common: {
    freePlan: 'Free Plan',
    reactivate: 'Reactivate',
    draft: 'Draft',
    save: 'Save',
    cancel: 'Cancel',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    selectedLabel: 'selected',
    addSelection: 'Add selection',
    adding: 'Adding…',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    close: 'Close',
    loading: 'Loading...',
    copy: 'Copy',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    search: 'Search',
    filter: 'Filter',
    reset: 'Reset',
    refresh: 'Refresh',
    validate: 'Validate',
    saveChanges: 'Save changes',
    unknownChannel: 'Unknown channel',
    confirmDeleteAllData: 'Are you sure you want to delete all data?',
    type: 'Type',
    weekDays: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
    characters: 'characters',
    viewSource: 'View source',
    others: 'others',
    or: 'or',
    optional: 'optional',
  },

  // Relative time
  time: {
    justNow: 'Just now',
    minutesAgo: '{count}m ago',
    hoursAgo: '{count}h ago',
    daysAgo: '{count}d ago',
    neverSynced: 'Never synced',
  },

  // Source types for traceability
  sourceTypes: {
    rss: 'RSS Feed',
    rss_feed: 'RSS Feed',
    youtube: 'YouTube',
    video: 'Video',
    website: 'Website',
    url: 'URL',
    raw_text: 'Plain text',
    text: 'Text',
    document: 'Document',
    pdf: 'PDF',
    podcast: 'Podcast',
    unknown: 'Source',
  },

  // Session expired
  session: {
    expiredTitle: 'Session expired',
    expiredDescription: 'Your session has expired. Please log in again.',
  },

  // YouTube help for SourceForm
  youtubeHelp: {
    title: '💡 How to find the YouTube channel ID:',
    step1: 'Go to the YouTube channel page',
    step2: 'The ID is in the URL:',
    step3: 'Or right-click → "View page source" → Search for "channelId"',
    note: 'The ID always starts with "UC" followed by 22 characters.',
    rawTextHelp: 'Paste your internal notes, monitoring information, or any content you want to use for post generation.',
  },

  // Generic UI Components
  ui: {
    sidebar: {
      title: 'Sidebar',
      mobileDescription: 'Displays the mobile sidebar',
    },
    carousel: {
      previous: 'Previous slide',
      next: 'Next slide',
    },
    pagination: {
      more: 'More pages',
      next: 'Next',
      previous: 'Previous',
    },
    dialog: {
      close: 'Close',
    },
  },

  // Account selection modal (headless OAuth)
  accountSelection: {
    title: 'Connect {platform}',
    loading: 'Loading...',
    noOptions: 'No options available.',
    retryButton: 'Try again',
    connectButton: 'Connect',
    connecting: 'Connecting...',
    loadError: 'Unable to load options. Please try again.',
    connectError: 'Connection error. Please try again.',
    subtitles: {
      linkedin: 'Choose how you want to publish',
      facebook: 'Choose the page to publish from',
      pinterest: 'Choose the board to connect',
      googlebusiness: 'Choose your establishment',
      snapchat: 'Choose your public profile',
      default: 'Choose an account',
    },
  },

  // OAuth / Social network connection
  oauth: {
    connect: 'Connect {{network}}',
    connecting: 'Connecting...',
    disconnect: 'Disconnect',
    refresh: 'Refresh token',
    notSupported: 'OAuth not available',
    confirmDisconnect: 'Are you sure you want to disconnect this account?',
    confirmDisconnectTitle: 'Disconnect account',
    confirmDisconnectDescription: 'Are you sure you want to disconnect this account? Your history will not be deleted.',
    confirmDisconnectAction: 'Disconnect',
    connected: 'Connected',
    needsRefresh: 'Token expired',
    expired: 'Not connected',
    error: 'Connection error',
    success: 'Connected!',
    selectPage: 'Select a page',
    selectAccount: 'Select an account',
    noPages: 'No Facebook pages found',
    noAccounts: 'No Instagram accounts found',
    pageRequired: 'You must select a page to post',
    accountRequired: 'You must select an Instagram account to post',
    processing: 'Connecting...',
    processingMessage: 'Please wait while we finalize the connection.',
    successMessage: 'Your account has been connected successfully. This window will close automatically.',
  },

  // Zernio / Social network connection via Zernio (getlate.dev)
  socials: {
    connect: 'Connect account',
    connecting: 'Connecting…',
    connected: 'Connected',
    disconnect: 'Disconnect',
    reconnect: 'Reconnect',
    refreshConnection: 'Refresh connection',
    expired: 'Token expired',
    notConnected: 'Not connected',
    connectNow: 'Connect now',
    connectPrompt: 'Connect this network to publish your posts',
    connectReminderTitle: 'Channel created — connect it now',
    connectReminderBody: 'Connect your network so you can publish the generated posts.',
    notSupported: 'Not supported',
    confirmDisconnect: 'Are you sure you want to disconnect this social account?',
    confirmDisconnectTitle: 'Disconnect account',
    confirmDisconnectDescription: 'Are you sure you want to disconnect this social account? Your history will not be deleted.',
    confirmDisconnectAction: 'Disconnect',
    manage: 'Manage connection',
    callbackProcessing: 'Finalizing connection…',
    callbackSuccess: 'Account connected successfully! This window will close.',
    callbackError: 'Connection error. Please try again.',
    channelsUsed: 'channels used',
    tokenExpiresIn: 'Expires in {{days}} days',
    tokenExpiresSoon: 'Token expiring soon',
    reconnectRequired: 'Reconnection required',
    telegramConnected: 'Telegram connected',
    telegramCodeExpired: 'Code expired, please try again',
    codeCopied: 'Code copied',
    telegramSetup: 'Telegram Connection',
    telegramInstructions: 'Instructions:',
    telegramStep1: 'Add @LateScheduleBot as an administrator in your channel/group',
    telegramStep2: 'Open a private chat with @LateScheduleBot',
    telegramStep3: 'Send the code above followed by @yourchannel',
    telegramStep4: 'Wait for automatic confirmation above',
    openBot: 'Open @LateScheduleBot',
    profileAlreadyExists: 'A profile with this name already exists on this platform.',
    connectionError: 'Error connecting to social account.',
    popupBlocked: 'Pop-up blocked. Allow pop-ups for this site, then try again.',
  },

  // Available languages
  languages: {
    fr: 'Français',
    en: 'English',
    es: 'Español',
    de: 'Deutsch',
    it: 'Italiano',
    pt: 'Português',
    nl: 'Nederlands',
    pl: 'Polski',
    ru: 'Русский',
    zh: '中文',
    ja: '日本語',
    ar: 'العربية',
  },

  // WebSocket Messages (standardized i18n keys from backend)
  quota: {
    aiCredits: 'AI Credits',
    used: 'used',
    warning: 'Warning: high AI credit usage',
    exceeded: 'Quota exceeded — renewal on the 1st of next month',
    accountTitle: 'My account',
    creditsSection: 'AI Usage',
    creditsDescription: 'Your AI credit usage for the current period.',
    renewal: 'Automatic renewal on the 1st of each month.',
    quotaExceededToast: 'AI credits exhausted. Automatic renewal on the 1st of next month.',
    quotaExceededTopupToast: 'AI credits exhausted. Top up your credits or wait for renewal on the 1st of the month.',
    quotaExceededTooltip: 'AI quota exhausted — renewal on the 1st of the month.',
    quotaExceededTitle: 'AI Quota Exceeded',
    textBudget: 'Text budget',
    images: 'Images',
    videos: 'Videos',
    comingSoon: 'Coming soon',
    subscription: 'Subscription',
    of: 'of',
    // Topup
    purchasedCredits: 'Purchased credits',
    topupTitle: 'Top up your AI credits',
    topupDescription: 'Purchase additional credits for your account.',
    topupPlanInfo: 'Top up your AI credits',
    topupOneTime: 'One-time payment — persistent credits',
    topupBuyButton: 'Buy credits',
    topupClose: 'Close',
    topupLoading: 'Loading...',
    topupQuantity: 'Quantity',
    topupCreditsLabel: 'Credits',
    topupUnitPrice: 'Unit price',
    topupCreditsUnit: 'credits',
    topupCurrentBalance: 'Remaining purchased credits:',
    topupSuccessTitle: 'Top-up successful!',
    topupSuccessMessage: 'Your AI credits have been topped up successfully!',
    topupErrorTitle: 'Error',
    topupLoadError: 'Failed to load top-up information.',
    topupPurchaseError: 'Top-up failed. Please try again.',
    topupCancelledMessage: 'Top-up cancelled.',
    topupVatAtCheckout: 'VAT calculated at checkout',
  },
  ws: {
    fallbackStep: 'Processing…',
    generation: {
      started: 'Generating {{count}} post(s) for {{platform}}...',
      connecting: 'Connecting to server...',
      completed: '✓ {{count}} post(s) generated',
      failed: '❌ Error: {{error}}',
    },
    memory: {
      loading: 'Loading history...',
      loaded: '{{posts_count}} posts and {{hooks_count}} hooks loaded',
    },
    knowledge: {
      searching: 'Searching for relevant sources...',
      semantic_search: 'Semantic analysis in progress...',
      extracting_metadata: 'Extracting metadata...',
      extracting_facts: 'Extracting key facts...',
      found: '{{sources_count}} source(s) and {{facts_count}} fact(s) found',
      none: 'No relevant sources found',
    },
    writing: {
      started: 'Writing {{count}} post(s) for {{platform}}...',
      post: 'Writing post {{index}}/{{total}}...',
      shortening: 'Optimizing post {{index}} length...',
      completed: '{{count}} post(s) written',
      error: 'Writing error: {{error}}',
    },
    diversity: {
      checking: 'Checking diversity of {{count}} post(s)...',
      passed: '✓ {{valid_count}} post(s) validated',
      regenerating: '🔄 Regenerating {{invalid_count}} post(s) (attempt {{attempt}}/{{max_attempts}})...',
    },
    compliance: {
      validating: 'Validating {{count}} post(s)...',
      analyzing: 'Analyzing post {{index}}/{{total}}...',
      approved: '✓ {{count}} post(s) approved',
      needs_revision: '{{count}} post(s) need revision',
      iterating: '🔄 Iteration {{iteration}}/{{max_iterations}} in progress...',
    },
    finalize: {
      saving: 'Saving posts...',
    },
    fallback: {
      started: 'Fallback mode activated...',
      completed: '{{count}} post(s) generated (fallback mode)',
    },
  },

  billing: {
    title: 'Billing',
    subscription: 'Subscription',
    currentPlan: 'Current plan',
    email: 'Billing email',
    invoices: 'Invoices',
    noInvoices: 'No invoices yet.',
    downloadPdf: 'Download PDF',
    couponUsed: 'Promo code',
    promoCode: 'Promo code',
    promoCodePlaceholder: 'Enter your promo code',
    apply: 'Apply',
    toasts: {
      couponApplied: 'Promo code applied successfully.',
      couponError: 'Invalid or expired promo code.',
      pdfError: 'Unable to retrieve the invoice.',
      switchIntervalUpdated: 'Your subscription has been updated.',
      switchIntervalError: 'Unable to update your subscription. Please try again later.',
      cancelError: 'Unable to cancel your subscription. Please try again later.',
      referralCodeError: 'Unable to generate your referral code. Please try again later.',
    },
    billingInterval: 'Billing',
    monthly: 'Monthly',
    annual: 'Annual',
    switchToAnnual: 'Switch to annual',
    switchToMonthly: 'Switch to monthly',
    saveWithAnnual: 'Save ~17% with annual billing',
    switchIntervalConfirm: 'You will be redirected to the payment page to update your subscription.',
    cancelSubscription: 'Cancel my subscription',
    cancelConfirmTitle: 'Cancel your subscription?',
    cancelConfirmMessage: 'Your access will be maintained until the end of your current billing period.',
    cancelConfirmButton: 'Confirm cancellation',
    subscriptionCancelled: 'Subscription cancelled. Access maintained until end of period.',
    subscriptionEndsAt: 'Access until',
    statusCancelling: 'Cancelling',
    autoRenewal: 'Automatic renewal',
    autoRenewalActive: 'Active',
    // Payment failure alert
    paymentFailed: 'Payment failed',
    paymentFailedDescription: 'Update your payment method before {date} to keep your Pro access.',
    paymentFailedExpired: 'Your Pro access has been suspended due to a payment failure.',
    updatePaymentMethod: 'Update payment method',
    deleteAccount: 'Delete my account',
    deleteAccountTitle: 'Delete your account?',
    deleteAccountWarning: 'This action is irreversible. Your subscription will be cancelled and your account deactivated.',
    deleteAccountPasswordLabel: 'Confirm your password',
    deleteAccountPasswordPlaceholder: 'Enter your current password',
    deleteAccountConfirmButton: 'Delete permanently',
    deleteAccountSuccess: 'Account deleted successfully.',
    deleteAccountPasswordError: 'Incorrect password.',
    // Referral
    referralTitle: 'Referral',
    referralCode: 'Your referral code',
    referralCopied: 'Code copied!',
    referralCopyButton: 'Copy',
    referralCount: 'Referrals',
    referralCountOf: 'of',
    referralNoCode: 'Generate your code',
    referralGenerating: 'Generating...',
    myRewards: 'My rewards',
    noRewards: 'No rewards available',
    rewardUsed: 'Used',
    applyToRenewal: 'Apply to renewal',
    rewardApplied: 'Reward applied to your subscription!',
    rewardApplyError: 'Failed to apply reward',
    // Upgrade
    upgradePlan: 'Upgrade to a paid plan',
    choosePlan: 'Choose',
    upgradeTitle: 'Change subscription',
    selectInterval: 'Billing',
  },

  // V4-ACCOUNT-01 2026-05-29 — Account Tab labels & copy
  account: {
    roleUser: 'User',
    roleAdmin: 'Administrator',
    roleViewer: 'Viewer',
    roleAgency: 'Agency',
    roleAgencyOwner: 'Agency owner',
    roleAgencyAdmin: 'Agency admin',
    roleAgencyMember: 'Agency member',
    applyPromoCode: 'Apply promo code',
    referralDescription: 'Invite colleagues and earn credits for each confirmed sign-up.',
    referralLearnMore: 'Learn more',
  },

  // HTTP global errors (apiClient)
  errors: {
    forbidden: 'Access denied',
    forbiddenDescription: 'You do not have permission to perform this action.',
    tooManyRequests: 'Too many requests',
    tooManyRequestsDescription: 'You have reached the request limit. Please try again in a moment.',
    serverError: 'Server error',
    serverErrorDefault: 'A server error occurred.',
    // V4-APICLIENT-INTERCEPTOR 2026-05-29 — typed backend error mapping
    urlScrapingFailed: 'Unable to analyse URL',
    urlScrapingFailedDescription: 'The page content could not be fetched. Check the URL or try again later.',
    llmWorkerUnavailable: 'AI service momentarily unavailable',
    llmWorkerUnavailableDescription: 'The LLM worker is not responding. Try again shortly.',
    recursionLimitExceeded: 'Generation interrupted',
    recursionLimitExceededDescription: 'The agent reached its reasoning limit. Simplify the request or retry.',
    upstreamLlmTimeout: 'AI request timed out',
    upstreamLlmTimeoutDescription: 'The LLM provider took too long to respond. Please retry.',
    agencyPermissionsUnavailable: 'Permissions could not be checked',
    agencyPermissionsUnavailableDescription: 'Your access rights could not be verified right now. Please try again shortly.',
    badRequest: 'Invalid request',
    badRequestDescription: 'The submitted request could not be processed.',
  },

  carousel: {
    title: 'Carousel',
    generate: 'Generate a carousel',
    generating: 'Generating…',
    backgroundStarted: 'Carousel generation started',
    backgroundStartedDescription: 'You can close this window. The carousel will be attached to the post automatically.',
    editCarousel: 'Edit carousel',
    addCarousel: 'Add a carousel',
    removeCarousel: 'Remove carousel',
    fullRegenerationTitle: 'Full regeneration',
    fullRegenerationDescription: 'Launch a brand new version of the carousel to explore a different creative direction.',
    slideRegenerationTitle: 'Selected slide regeneration',
    selectedSlideLabel: 'Selected slide',
    clearInstructions: 'Clear',
    closeEditor: 'Close editor',
    deleteSectionTitle: 'Delete carousel',
    deleteSectionDescription: 'This detaches the carousel from the post and removes its stored files as well.',
    deleteConfirm: 'Confirm deletion',
    deleteSuccess: 'Carousel deleted',
    deleteError: 'Error deleting carousel',
    containsCarousel: 'Contains a carousel',
    slides: 'Slides',
    slide: 'Slide',
    slideOf: 'Slide {current} of {total}',
    aspectRatio: 'Format',
    nSlides: 'Number of slides',
    regenerateSlide: 'Regenerate this slide',
    regenerating: 'Regenerating…',
    instructions: 'Modification instructions',
    instructionsPlaceholder: 'E.g. Make this slide more impactful with a darker background…',
    exportPng: 'Export as PNG',
    exportPdf: 'Export as PDF',
    exportPdfSuccess: 'PDF generated',
    exportPdfError: 'Error generating PDF',
    exporting: 'Exporting…',
    exportSuccess: 'Carousel exported!',
    exportError: 'Export error',
    generationError: 'Error generating carousel',
    postNotFound: 'This post no longer exists. Refresh the page and try again.',
    reorderHint: 'Drag and drop to reorder slides',
    noSlides: 'No slides',
    previewTitle: 'Carousel preview',
    chatPlaceholder: 'Describe the carousel to generate…',
    sendMessage: 'Generate',
    ratio_1_1: 'Square (1:1)',
    ratio_4_5: 'Portrait (4:5)',
    ratio_9_16: 'Stories / Reels (9:16)',
  },
  newsletterTemplate: {
    documentTitle: 'Newsletter',
    heading: '🚀 Your digest',
    subtitle: 'The essential news, straight to your inbox',
    introHeading: 'In this issue',
    introBody: 'Here is the selection of news and trends we picked for you this period.',
    ctaHeading: 'Want to go further?',
    ctaLabel: 'Discover our solutions',
    postImageAlt: 'Post illustration',
    rights: 'All rights reserved',
    subscriptionNotice: 'You are receiving this email because you subscribed to our newsletter.',
  },
};